import React from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  deleteCustomer,
  updateCustomerActive,
  clearCustomerMessages,
} from "../../../store/customer/customerSlice";
import { getCustomerDisplayName, isCustomerActive } from "../utils/customerHelpers";

const DELETE_STEP = {
  DEACTIVATE: "deactivate",
  DELETE: "delete",
};

const DeleteCustomerDialog = ({ open, onClose, customer, onSuccess, onDeactivateSuccess }) => {
  const dispatch = useDispatch();
  const {
    isDeletingCustomer,
    deleteCustomerSuccess,
    deleteCustomerError,
    isUpdatingCustomerActive,
    updateCustomerActiveSuccess,
    updateCustomerActiveError,
  } = useSelector((state) => state.customer);

  const [step, setStep] = React.useState(DELETE_STEP.DELETE);
  const [localCustomer, setLocalCustomer] = React.useState(customer);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("info");

  const customerName = getCustomerDisplayName(localCustomer || customer);

  React.useEffect(() => {
    if (open) {
      setLocalCustomer(customer);
      setStep(isCustomerActive(customer) ? DELETE_STEP.DEACTIVATE : DELETE_STEP.DELETE);
      dispatch(clearCustomerMessages());
    }
  }, [open, customer, dispatch]);

  const handleClose = () => {
    if (!isDeletingCustomer && !isUpdatingCustomerActive) {
      onClose();
    }
  };

  const handleDeactivateAndContinue = () => {
    const orgcode = localCustomer?.orgcode || customer?.orgcode;
    if (orgcode) {
      dispatch(updateCustomerActive({ orgcode, active: false }));
    }
  };

  const handlePermanentDelete = () => {
    const orgcode = localCustomer?.orgcode || customer?.orgcode;
    if (orgcode) {
      dispatch(deleteCustomer(orgcode));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarSeverity("info");
    dispatch(clearCustomerMessages());
  };

  React.useEffect(() => {
    if (!open || !updateCustomerActiveSuccess) return;

    if (step === DELETE_STEP.DEACTIVATE) {
      setLocalCustomer((prev) => ({
        ...(prev || customer),
        is_active: false,
        isActive: false,
        active: false,
      }));
      setStep(DELETE_STEP.DELETE);
      dispatch(clearCustomerMessages());
      onDeactivateSuccess?.();
    }
  }, [
    updateCustomerActiveSuccess,
    open,
    step,
    customer,
    dispatch,
    onDeactivateSuccess,
  ]);

  React.useEffect(() => {
    if (!open || !updateCustomerActiveError) return;

    if (step === DELETE_STEP.DEACTIVATE) {
      const errorMessage =
        typeof updateCustomerActiveError === "string"
          ? updateCustomerActiveError
          : updateCustomerActiveError.data ||
            updateCustomerActiveError.message ||
            "Failed to deactivate customer.";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [updateCustomerActiveError, open, step]);

  React.useEffect(() => {
    if (deleteCustomerSuccess) {
      const message =
        deleteCustomerSuccess.data ||
        deleteCustomerSuccess.message ||
        "Customer deleted successfully.";
      setSnackbarMessage(message);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onSuccess();
      onClose();
      dispatch(clearCustomerMessages());
    }
  }, [deleteCustomerSuccess, onClose, onSuccess, dispatch]);

  React.useEffect(() => {
    if (deleteCustomerError) {
      const errorMessage =
        typeof deleteCustomerError === "string"
          ? deleteCustomerError
          : deleteCustomerError.data ||
            deleteCustomerError.message ||
            JSON.stringify(deleteCustomerError);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [deleteCustomerError]);

  const isDeactivateStep = step === DELETE_STEP.DEACTIVATE;

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="delete-customer-dialog"
        aria-describedby="dialog-for-deleting-customer"
      >
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 520 },
            maxWidth: 520,
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#F5F5F5",
              padding: "16px 24px",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#000",
                fontSize: "1.125rem",
              }}
            >
              {isDeactivateStep ? "Deactivate Before Delete" : "Delete Permanently"}
            </Typography>
            <IconButton
              onClick={handleClose}
              disabled={isDeletingCustomer || isUpdatingCustomerActive}
              sx={{
                color: "#000",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3 }}>
            {isDeactivateStep ? (
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  color: "#1e293b",
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                }}
              >
                <strong>{customerName}</strong> is currently active and must be
                deactivated before it can be deleted permanently.
                <br />
                <br />
                Would you like to deactivate this customer now? Deactivating will
                suspend your Billing and Invoice Generation. You can reactivate the
                customer at any time from this page.
              </Typography>
            ) : (
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  color: "#1e293b",
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                }}
              >
                You are about to permanently delete{" "}
                <strong>{customerName}</strong>. This action cannot be undone and
                all associated customer data will be removed.
              </Typography>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isDeletingCustomer || isUpdatingCustomerActive}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>
              {isDeactivateStep ? (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleDeactivateAndContinue}
                  disabled={isUpdatingCustomerActive}
                  startIcon={
                    isUpdatingCustomerActive ? <CircularProgress size={20} /> : null
                  }
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                  }}
                >
                  {isUpdatingCustomerActive
                    ? "Deactivating..."
                    : "Yes, Deactivate"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  onClick={handlePermanentDelete}
                  disabled={isDeletingCustomer}
                  startIcon={isDeletingCustomer ? <CircularProgress size={20} /> : null}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                  }}
                >
                  {isDeletingCustomer ? "Deleting..." : "Delete Permanently"}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

DeleteCustomerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
  onDeactivateSuccess: PropTypes.func,
};

export default DeleteCustomerDialog;
